// client/src/components/guards/OwnerUnitGuard.tsx
// 868mbzt38 — D1 (anti-bucle). Latches the units query so a transient 4xx
// or 5xx renders a single stable Retry notice instead of bouncing between
// skeleton and portal children. The owners with several units still get
// redirected to the picker once units load.

import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { PageSkeleton } from '@/components/PageSkeleton';
import { QueryErrorState } from '@/components/ui/QueryErrorState';
import { saveReturnPath } from '@/lib/returnPath';
import { useOwnerUnit } from '@/features/owner-portal/context/OwnerUnitContext';

export function OwnerUnitGuard({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { allUnits, isLoading, hasUserSelected, loadError, isRetrying, retry } =
    useOwnerUnit();

  const needsSelection = !isLoading && allUnits.length > 1 && !hasUserSelected;

  useEffect(() => {
    if (needsSelection && location !== '/owner/unit-select') {
      saveReturnPath(location);
      setLocation('/owner/unit-select');
    }
  }, [needsSelection, location, setLocation]);

  // 868mbzt38 — error latch wins over loading. With the latched error in
  // place, the notice stays until data arrives; without it, the guard
  // would swap children and skeleton on every Retry.
  if (loadError !== null) {
    // 71 callers de QueryErrorState pasan `error={query.error}` (de tipo
    // `Error | null`); ese contrato no se cambia aquí — el guard pasa el
    // error real y `QueryErrorState` decide por `instanceof`.
    return (
      <div
        data-testid="owner-unit-load-error"
        className="flex flex-col items-center justify-center gap-3 py-16"
      >
        <QueryErrorState
          error={loadError}
          isRetrying={isRetrying}
          onRetry={retry}
        />
      </div>
    );
  }

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (needsSelection && location !== '/owner/unit-select') {
    return <PageSkeleton />;
  }

  return <>{children}</>;
}
